import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagic } from '@fortawesome/free-solid-svg-icons'
import { ensureAssistFeature, requestItemInsight } from '../../../store/engagement/assist'
import ZyniMarkdown from '../ZyniMarkdown'
import './ZyniItemInsight.scss'

interface ZyniItemInsightProps {
    organizationId?: number,
    teamId: number,
    datumId: number,
}

/**
 * "Ask Zyni about this item" - Organizational Intelligence Proof #1. Shown on an
 * Engage Deliverable; the backend combines the item's recorded facts, calculated
 * conditions, and any current TOMI that appears related, and Zyni explains what may
 * deserve attention. Renders nothing unless the feature is enabled for this user.
 */
export const ZyniItemInsight = ({ organizationId, teamId, datumId }: ZyniItemInsightProps) => {
    const dispatch = useDispatch()
    const enabled = useSelector((state: any) => state.engagement_assist?.itemInsight?.enabled === true)
    const [loading, setLoading] = useState(false)
    const [text, setText] = useState('')
    const [error, setError] = useState('')

    useEffect(() => {
        dispatch(ensureAssistFeature() as any)
    }, [dispatch])

    if (!enabled) {
        return null
    }

    const ask = async () => {
        setLoading(true)
        setError('')
        try {
            const data: any = await dispatch(requestItemInsight({
                organization_id: organizationId,
                team_id: teamId,
                datum_id: datumId,
            }) as any)
            setText(String(data?.insight?.text || ''))
        } catch (err: any) {
            setError(err?.response?.data?.error?.message || 'Zyni could not review this item right now. Please try again in a moment.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="zyni-item-insight" data-testid="zyni-item-insight">
            <button
                type="button"
                className="zyni-item-insight__trigger"
                onClick={ask}
                disabled={loading}
                title="Ask Zyni what may deserve attention on this item"
            >
                <FontAwesomeIcon icon={faMagic} className="zyni-item-insight__icon" />
                <span>{text ? 'Ask Zyni again' : 'Ask Zyni about this item'}</span>
            </button>

            {loading && <div className="zyni-item-insight__status" role="status">Zyni™ is reviewing this item…</div>}

            {!loading && error && <div className="zyni-item-insight__error" role="alert">{error}</div>}

            {!loading && !error && text && (
                <div className="zyni-item-insight__answer" data-testid="zyni-item-insight-answer">
                    <ZyniMarkdown text={text} />
                </div>
            )}
        </div>
    )
}
